import { useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { IconList } from '@components/icons/list';
import { IconPinned } from '@components/icons/pinned';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIsMobile } from '@hooks/use-is-mobile';

import { useJobOrderingList, usePinJob, useUnPinJob } from 'api/jobs/hooks';
import { JobOrderingData } from 'api/jobs/requests';
import { useStore } from 'models/Provider';

import { DraggableJob, JobCard } from './draggable-job';
import { DraggingState, EmptyCard } from './ordering';

export const OrderJobs = () => {
  const jobQuery = useJobOrderingList();

  if (!jobQuery.isSuccess) {
    return <RelativeSpinner />;
  }
  return <RenderJobs jobs={jobQuery.data} />;
};

export const RenderJobs = (props: { jobs: JobOrderingData[]; enterprise?: boolean }) => {
  const { jobs, enterprise = false } = props;
  const { t } = useStore();
  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);
  const [pinnedJobs, setPinnedJobs] = useState<JobOrderingData[]>(jobs.filter((job) => job.pinned));

  useEffect(() => {
    setPinnedJobs(jobs.filter((job) => job.pinned === true));
  }, [jobs]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const isMobile = useIsMobile();
  const pin = usePinJob();
  const unpin = useUnPinJob();

  const jobIds = jobs.map((job) => String(job.id));
  const hasPinnedJobs = pinnedJobs.length > 0;
  const unpinnedJobs = jobs.filter((job) => !job.pinned);
  const hasUnpinnedJobs = unpinnedJobs.length > 0;

  const handlePin = (jobId: number, placement: 'top' | 'bottom' | 'new') => {
    let order = 1;
    const job = jobs.find((job) => job.id === jobId);
    if (placement === 'bottom' || placement === 'new') {
      const pinnedJobs = jobs.filter((job) => job.pinned === true);
      if (pinnedJobs.length > 0) {
        const pinnedJobsOrder = pinnedJobs.map((job) => job.order);
        order = Math.max(...pinnedJobsOrder);
        if (placement === 'new') {
          order += 1;
        }
      }
    }
    if (job?.order !== order) {
      pin.mutate({ jobId: jobId, ordering: order });
    }
  };

  const draggingJob = draggingItem ? jobs[draggingItem.index] : null;

  function onDragStart(event: DragStartEvent) {
    if (isMobile) return;
    const { active } = event;
    const order = active.data.current?.order;
    setDraggingItem({
      order: order,
      index: active.data.current?.sortable.index,
      id: active.data.current?.id,
    });
  }

  async function onDragEnd(event: DragEndEvent) {
    if (!draggingItem) return;
    const { active, over } = event;
    setDraggingItem(null);
    if (!over || active.id === over.id) return;

    const newOrder = over.data.current?.order;
    const jobId = active.id;
    setPinnedJobs((items) => {
      const activeIndex = items.findIndex((item) => String(item.id) === active.id);
      const overIndex = items.findIndex((item) => String(item.id) === over.id);
      return arrayMove(items, activeIndex, overIndex);
    });
    pin.mutate({ jobId: +jobId, ordering: newOrder });
  }

  function onDragCancel() {
    setDraggingItem(null);
  }

  return (
    <>
      <Stack space={2}>
        <Stack space={4} orientation="horizontal">
          <Text
            font="semibold14"
            color="purple100"
            text={
              enterprise
                ? t('company.settings.joborder.departments.pinned')
                : t('company.settings.joborder.pinned')
            }
          />
          <Text font="regular14" color="purple50" text={t('company.settings.joborder.manually')} />
        </Stack>
        {hasPinnedJobs ? (
          <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
          >
            <DragOverlay>{draggingJob && <JobCard job={draggingJob} />}</DragOverlay>
            <SortableContext items={jobIds} strategy={verticalListSortingStrategy}>
              {pinnedJobs.map((job) => (
                <Stack space={2} key={job.id}>
                  <DraggableJob
                    job={job}
                    handlePin={handlePin}
                    unpin={unpin.mutate}
                    isPinned
                    isDragging={draggingJob?.id === job.id}
                  />
                </Stack>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <EmptyCard
            text={t('company.settings.joborder.pinned.none')}
            icon={<IconPinned color="purple60" />}
            space={enterprise ? 4 : 6}
          />
        )}
      </Stack>
      <Separator />
      <Stack space={2}>
        <Stack space={4} orientation="horizontal">
          <Text
            font="semibold14"
            color="purple100"
            text={t('company.settings.joborder.unpinned')}
          />
          <Text
            font="regular14"
            color="purple50"
            text={t('company.settings.joborder.unpinned.order')}
          />
        </Stack>
        <Stack space={2}>
          {hasUnpinnedJobs ? (
            <>
              {unpinnedJobs.map((job) => (
                <Stack space={2} key={job.id}>
                  <DraggableJob job={job} handlePin={handlePin} unpin={unpin.mutate} />
                </Stack>
              ))}
            </>
          ) : (
            <EmptyCard
              text={t('company.settings.joborder.unpinned.none')}
              icon={<IconList color="purple80" />}
              space={enterprise ? 4 : 6}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
};
